import {
    AddBookmarkRequest,
    AddBookmarkRequest_ReverseAction,
    type BookmarkItem,
    DeleteBookmarkRequest,
    ReadingListType,
    SetBookmarkSettingsRequest,
} from '@app/_proto/Protos/bookmarks';
import { BookmarksClient } from '@app/_proto/Protos/bookmarks.client';
import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { BoolValue } from '@app/_proto/google/protobuf/wrappers';
import { ReactComponent as Pin } from '@app/assets/pin.svg';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import { useAuth } from '@app/libs/auth';
import { GrpcError } from '@app/libs/grpc';
import { useHttp } from '@app/libs/http';
import { batch } from '@app/libs/utils';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { Alert, AlertTitle, Button, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

//#region : Global constants and types

/** Custom. Used to mark bookmark state */
type BookmarkState =
    | 'idle'
    | 'backtracking'
    | 'bookmarking'
    | 'bookmarked'
    | 'undo'
    | 'deleted'
    | 'error'
    | 'pinned'
    | 'already-bookmarked';

const AlertBookmarkStates: BookmarkState[] = ['bookmarked', 'error'];

//#endregion : Global constants and types

//#region : Main component

/** Custom. Main component parameters type */
export type ChapterBookmarkProps = JSX.IntrinsicElements['div'] & {
    novel: NovelItem | undefined;
    chapter: ChapterItem | undefined;
    contentRef?: Element | null;
};

/** Custom. Bookmarks a given chapter and shows message. */
export default function ChapterBookmark({ novel, chapter, contentRef, ...rest }: ChapterBookmarkProps) {
    //#region : Variables, functions and api calls

    //General variables
    const { user } = useAuth();
    const { grpcRequest } = useHttp();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const bookmarkEnabled = searchParams.get('bookmark') !== '0';

    const { isChapterLocked } = useChapterLockStatus({ novel, chapter });

    const [notifOpen, setNotifOpen] = useState(false);
    const [bookmarkState, setBookmarkState] = useState<BookmarkState>('idle');
    const prevBookmark = useRef<BookmarkItem | null>(null);
    const currentBookmark = useRef<BookmarkItem | null>(null);
    const abortController = useRef<AbortController | null>(null);

    //#region : Bookmark actions
    useEffect(() => {
        setBookmarkState('idle');
    }, [chapter?.entityId]);

    /**
     * Custom.
     *
     * Makes the server call to set the bookmark.
     * Aborts the previous request if it is still in progress.
     */
    const setBookmark = useCallback(
        async (id: number, force: boolean) => {
            let abort = abortController.current;

            if (abort) {
                abort.abort();
            }

            abort = abortController.current = new AbortController();

            const request = AddBookmarkRequest.create({
                chapterId: id,
                reverseAction: force ? AddBookmarkRequest_ReverseAction.Replace : AddBookmarkRequest_ReverseAction.Fail,
            });

            return await grpcRequest(
                BookmarksClient,
                c => c.addBookmark,
                request,
                {},
                {
                    abort: abort.signal,
                }
            );
        },
        [grpcRequest]
    );

    /**
     * Custom.
     *
     * Makes the server call to delete the bookmark.
     */
    const deleteBookmark = useCallback(
        async (id: number) => {
            const request = DeleteBookmarkRequest.create({
                id: {
                    oneofKind: 'novelId',
                    novelId: id,
                },
                listType: ReadingListType.Current,
            });

            return await grpcRequest(BookmarksClient, c => c.deleteBookmark, request);
        },
        [grpcRequest]
    );

    /**
     * Custom.
     *
     * Handles delete button click.
     */
    const handleDeleteBookmark = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();

            if (!chapter) {
                return;
            }

            try {
                await deleteBookmark(chapter.novelId);

                batch(() => {
                    setNotifOpen(false);
                    setBookmarkState('deleted');
                });
            } catch (e) {
                setBookmarkState('error');

                throw e;
            }
        },
        [chapter, deleteBookmark]
    );

    /**
     * Custom.
     *
     * Bookmarks the chapter and sets the bookmark state.
     */
    const doBookmark = useCallback(
        async (id: number, force: boolean) => {
            try {
                setBookmarkState('bookmarking');

                const response = await setBookmark(id, force);

                currentBookmark.current = response.currentBookmark || null;
                prevBookmark.current = response.previousBookmark || null;

                if (response.result) {
                    if (prevBookmark.current?.chapter?.entityId !== id) {
                        setBookmarkState('bookmarked');

                        queryClient.invalidateQueries(['bookmarks', user?.id]);
                    } else if (prevBookmark.current?.chapter?.entityId === id) {
                        setBookmarkState('already-bookmarked');
                    } else {
                        setBookmarkState('error');
                    }
                } else {
                    setBookmarkState('pinned');
                }
            } catch (e) {
                if (e instanceof GrpcError) {
                    if (e.response?.metadata.reason === 'reverse_action') {
                        setBacktracking();
                    }
                } else {
                    setBookmarkState('error');
                }

                throw e;
            }
        },
        [queryClient, setBookmark, user?.id]
    );
    //#endregion : Bookmark actions

    //#region : Notification actions

    //Open up the bookmark alert when the chapter is successfully bookmarked.
    useEffect(() => {
        if (AlertBookmarkStates.includes(bookmarkState)) {
            setNotifOpen(true);
        }
    }, [bookmarkState]);

    /**
     * Custom.
     *
     * Closes the bookmark notification.
     */
    const handleCloseNotif = useCallback(() => {
        setNotifOpen(false);
    }, []);

    /**
     * Custom.
     *
     * Undo the bookmark. Reverts to the previous bookmark.
     */
    const handleUndoNotification = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();

            if (!prevBookmark.current) {
                return;
            }

            try {
                batch(() => {
                    setNotifOpen(false);
                    setBookmarkState('undo');
                });

                if (prevBookmark.current?.chapter) {
                    await setBookmark(prevBookmark.current.chapter.entityId, true);
                }
            } catch (e) {
                setBookmarkState('error');

                throw e;
            }
        },
        [setBookmark]
    );

    const handleRetry = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();

            if (!chapter) {
                return;
            }

            doBookmark(chapter.entityId, true);
        },
        [chapter, doBookmark]
    );

    const handleUnlockBookmark = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();

            if (!chapter) {
                return;
            }

            const request = SetBookmarkSettingsRequest.create({
                novelId: chapter.novelId,
                locked: BoolValue.create({
                    value: false,
                }),
            });

            await grpcRequest(BookmarksClient, c => c.setBookmarkSettings, request);

            doBookmark(chapter.entityId, true);
        },
        [chapter, doBookmark, grpcRequest]
    );

    const setBacktracking = useCallback(() => {
        batch(() => {
            setBookmarkState('backtracking');
            setNotifOpen(true);
        });
    }, []);

    //#endregion : Notification actions

    useEffect(() => {
        const el = contentRef;

        let observer: IntersectionObserver | null = null;

        if (el && chapter && user && !isChapterLocked && bookmarkEnabled) {
            requestAnimationFrame(() => {
                const { bottom } = el.getBoundingClientRect();
                const offset = bottom + window.scrollY;

                observer = new IntersectionObserver(
                    entries => {
                        const [{ isIntersecting }] = entries;

                        if (isIntersecting) {
                            observer?.disconnect();
                            observer = null;

                            if (
                                currentBookmark.current?.novelId !== chapter.novelId &&
                                chapter.relatedUserInfo?.isBeforeBookmarkOffset?.value
                            ) {
                                setBacktracking();
                            } else if (
                                currentBookmark.current?.novelId === chapter.novelId &&
                                chapter.offset < (currentBookmark.current?.chapter?.offset ?? -1)
                            ) {
                                setBacktracking();
                            } else {
                                doBookmark(chapter.entityId, false);
                            }
                        }
                    },
                    {
                        rootMargin: `${offset / 2}px 0px ${offset / 2 - 1}px 0px`,
                        threshold: 1,
                    }
                );

                observer.observe(el);
            });
        }

        return () => {
            observer?.disconnect();
        };
    }, [bookmarkEnabled, chapter, contentRef, doBookmark, isChapterLocked, user, setBacktracking]);

    //#endregion : Variables, functions and api calls

    if (!notifOpen) {
        return null;
    }

    return (
        <div {...rest}>
            <Alert
                onClose={handleCloseNotif}
                severity={bookmarkState === 'error' ? 'error' : bookmarkState === 'pinned' ? 'warning' : 'success'}
                icon={
                    bookmarkState === 'error' ? (
                        <Error />
                    ) : bookmarkState === 'bookmarking' ? (
                        <CircularProgress className="text-blue-300" size={16} />
                    ) : bookmarkState === 'backtracking' ? (
                        <Warning />
                    ) : bookmarkState === 'pinned' ? (
                        <Pin className="self-center" />
                    ) : (
                        <CheckCircle className="text-blue" />
                    )
                }
                action={
                    <div
                        className={clsx('mx-[20px]', {
                            'pointer-events-none opacity-50': bookmarkState === 'bookmarking',
                        })}
                    >
                        {bookmarkState === 'error' ? (
                            <Button
                                variant="text"
                                color="secondary"
                                className="text-[14px] font-semibold dark:text-white !no-underline !p-0"
                                onClick={handleRetry}
                            >
                                Retry
                            </Button>
                        ) : bookmarkState === 'pinned' ? (
                            <Button
                                variant="text"
                                color="secondary"
                                className="text-[14px] font-semibold dark:text-white !no-underline !p-0"
                                onClick={handleUnlockBookmark}
                            >
                                Unpin
                            </Button>
                        ) : bookmarkState === 'backtracking' ? (
                            <Button
                                variant="text"
                                color="secondary"
                                className="text-[14px] font-semibold dark:text-white !no-underline !p-0"
                                onClick={handleRetry}
                            >
                                Bookmark
                            </Button>
                        ) : !!prevBookmark.current?.chapter ? (
                            <Button
                                variant="text"
                                color="secondary"
                                className="text-[14px] font-semibold dark:text-white !no-underline !p-0"
                                onClick={handleUndoNotification}
                            >
                                Undo
                            </Button>
                        ) : (
                            <Button
                                variant="text"
                                color="secondary"
                                className="text-[14px] font-semibold dark:text-white !no-underline"
                                onClick={handleDeleteBookmark}
                            >
                                Remove
                            </Button>
                        )}
                    </div>
                }
            >
                <AlertTitle className="mb-0">
                    {bookmarkState === 'error'
                        ? 'An error occurred updating your bookmark!'
                        : bookmarkState === 'pinned'
                          ? 'Your bookmark is pinned on a different chapter!'
                          : bookmarkState === 'backtracking'
                            ? 'You are currently reading an older chapter than the one currently bookmarked.'
                            : bookmarkState === 'bookmarking'
                              ? 'Processing...'
                              : 'Chapter marked as read!'}
                </AlertTitle>
            </Alert>
        </div>
    );
}
//#endregion : Main component
